"use client";

// MiniNav — the navigator, small, for the head of a sheet.
//
// The same grammar as the dashboard's navigator, at a size that fits a sheet:
//   · the CRUMB: where this sits ("stories / innernet / chapter 4"). BrandCrumb's
//     segment grammar: every label in ink, "/" in --ink-4, the last is where you
//     are, the earlier ones step back. Unlike BrandCrumb it stays on a phone (a
//     sheet has no ≡ to carry the way back). When the trail does not fit, the
//     earliest places fold away WHOLE and a "…" steps back to the nearest one
//     (etherNav crumbFit): a way back is never cut to a sliver ("br…"); only
//     where you are gives, and ellipsises last.
//   · the STEPS: the round ‹ › and "2 of 9" between them, for the siblings
//     either side (the list wraps, as the navigator's words do). ← → step too.
//   · the TICKER (optional): the siblings as words, the active one far left in
//     full ink, the rest trailing and fading; drag 4px to start, 44px to commit,
//     tap a word to go there. OsNavigator's arithmetic, one size down.
//   · the SECTIONS: what is open, as chips in ONE row (law_one_chip_row).
//
// Two ways to mount it:
//   1. inside a sheet, the usual way: hand the props to EtherModal's `nav` and
//      it seats every part in its head (crumb + steps on the close button's row,
//      the ticker under it, your `header` next, the chips last), handles ← → in
//      its one key handler, slides the body when `stepKey` or `section` changes,
//      and adds a sideways swipe on the body for touch.
//
//        <EtherModal open onClose={close} labelledBy={titleId} header={…}
//          nav={{
//            crumb: [{ label: "repo", onClick: close }, { label: "branches" }, { label: name }],
//            sections: [{ id: "story", label: "story" }, { id: "files", label: "files", n: 12 }],
//            section: tab, onSection: setTab,
//            prev: { label: "previous branch", name: prevName, onClick: toPrev },
//            next: { label: "next branch", name: nextName, onClick: toNext },
//            position: { index: 2, total: 9 },
//            stepKey: branchId,                     // the body slides when this changes
//            siblings: branches.map((b) => ({ key: b.id, label: b.name })),  // optional ticker
//            active: branchId, onPick: (key) => setBranch(key),
//          }}>
//          …the scrolling body…
//        </EtherModal>
//
//   2. standalone (anywhere else): <MiniNav {...same props} />. It then owns
//      ← → itself (a window listener, `keys` to turn it off).
//
// `siblingSteps(list, activeKey, keyOf)` from ../ether/etherNav gives prev,
// next and the position for a list in one call.
//
// Privacy: every label here may be memory (a project's name, a chat's first
// question). Labels are rendered as TEXT only, never in an attribute; the steps'
// accessible names are visually hidden text inside the buttons, not aria-label.
//
// Styles: ./mininav.css (class prefix mn-). Words reuse .os-sib-word (globals)
// so the proximity lift (law_nav_proximity) answers here too.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TICKER_DRAG_START, crumbFit, stepFromKey, tickerStep, tickerWords, wrapIndex } from "../ether/etherNav";
import "./mininav.css";

export interface MiniNavCrumb {
  label: string;
  /** a crumb you can step back to; the last crumb is where you are */
  onClick?: () => void;
}

export interface MiniNavSection {
  id: string;
  label: string;
  /** a count shown quietly beside the label */
  n?: number | null;
}

export interface MiniNavStep {
  /** a plain word for what the step does ("previous project"), read by a screen reader */
  label: string;
  /** the sibling's own name, when it has one: read by a screen reader as text, never an attribute */
  name?: string | null;
  onClick: () => void;
}

export interface MiniNavSibling {
  key: string;
  label: string;
}

export interface MiniNavProps {
  crumb: MiniNavCrumb[];
  sections?: MiniNavSection[];
  section?: string;
  onSection?: (id: string) => void;
  prev?: MiniNavStep | null;
  next?: MiniNavStep | null;
  /** "3 of 12" between the steps (index is 0-based) */
  position?: { index: number; total: number } | null;
  /** ← / → step between siblings while this nav is mounted (default true when prev/next exist).
   *  Seated in EtherModal the modal owns the keys and this is ignored. */
  keys?: boolean;
  /** id for the crumb's last item, so a dialog can be labelled by it */
  titleId?: string;
  className?: string;
  /** what is shown now (a sibling's key): EtherModal slides its body and resets its scroll when it changes */
  stepKey?: string | null;
  /** the siblings as words, a light ticker under the crumb (shown when there are two or more) */
  siblings?: MiniNavSibling[] | null;
  /** the sibling shown (defaults to stepKey) */
  active?: string | null;
  /** a word was tapped or the ticker dragged: go to `key` (dir is which way it lies) */
  onPick?: (key: string, dir: 1 | -1) => void;
  /** Seated in a sheet (law_modal_quiet, 2026-10-08): the ONE way back, and only
   *  while something is opened INSIDE the sheet (a quote, a commit). A sheet is
   *  about one thing and never walks back up the page's layers. */
  back?: { label: string; onClick: () => void } | null;
}

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

// ── the crumb + the steps ────────────────────────────────────────────────────

function Chev({ dir }: { dir: -1 | 1 }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d={dir < 0 ? "M10 3.5 5.5 8 10 12.5" : "M6 3.5 10.5 8 6 12.5"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function stepWords(step: MiniNavStep | null | undefined, fallback: string, none: string): string {
  if (!step) return none;
  const label = step.label?.trim() || fallback;
  return step.name ? `${label}, ${step.name}` : label;
}

/** where you are may give down to this many ems before a way back folds (it never gives below its own words) */
const HERE_FLOOR_EM = 6;
/** a phone sheet: once the trail is three deep its first place (the way out, which the close button already is) steps aside */
const PHONE = "(max-width: 480px)";

/**
 * Which places the crumb shows: the first index shown, and the first index
 * present at all (a phone drops the way out). Everything before `from` is
 * folded away whole; when that is more than the phone already dropped, a "…"
 * stands in for it. Measured before paint, again when the room changes.
 */
function useCrumbFit(crumb: MiniNavCrumb[]) {
  const olRef = useRef<HTMLOListElement | null>(null);
  const sig = crumb.map((c) => c.label).join("\u0000");
  const [fit, setFit] = useState<{ sig: string; from: number; first: number }>({ sig: "", from: 0, first: 0 });

  useIsoLayoutEffect(() => {
    const ol = olRef.current;
    if (!ol) return;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      const items = Array.from(ol.children).filter(
        (li): li is HTMLElement => li instanceof HTMLElement && !li.classList.contains("mn-crumb__fold"),
      );
      if (items.length !== crumb.length || !crumb.length) return;
      // every place is laid out at its natural width, even one folded away
      // (hidden, out of the flow), so the words can always be measured
      const widths = items.map((li) => li.querySelector<HTMLElement>(".mn-crumb__step, .mn-crumb__here")?.scrollWidth ?? 0);
      const olGap = parseFloat(getComputedStyle(ol).columnGap) || 0;
      const liGap = parseFloat(getComputedStyle(items[0]).columnGap) || 0;
      let sepW = 0;
      ol.querySelectorAll<HTMLElement>(".mn-crumb__sep").forEach((s) => (sepW = Math.max(sepW, s.offsetWidth)));
      const em = parseFloat(getComputedStyle(ol).fontSize) || 14;
      const foldEl = ol.querySelector<HTMLElement>(".mn-crumb__fold");
      const first = crumb.length >= 3 && typeof window.matchMedia === "function" && window.matchMedia(PHONE).matches ? 1 : 0;
      const from = crumbFit({
        widths,
        room: ol.clientWidth,
        sep: sepW + olGap + liGap,
        fold: foldEl ? foldEl.offsetWidth : em,
        hereFloor: HERE_FLOOR_EM * em,
        first,
      });
      setFit((cur) => (cur.sig === sig && cur.from === from && cur.first === first ? cur : { sig, from, first }));
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(ol);
    if ("fonts" in document) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      alive = false;
      ro?.disconnect();
    };
    // `crumb` is read through `sig` (its labels): a new array with the same words is the same trail
  }, [sig]);

  const fresh = fit.sig === sig;
  return { olRef, from: fresh ? fit.from : 0, first: fresh ? fit.first : 0 };
}

/**
 * The crumb's places, `from` onward (the ones before it folded away whole, a
 * "…" standing in for any past `first`). MiniNavLine measures `from`; this
 * only draws it, so the markup can be read without a layout.
 */
export function MiniNavTrail({
  crumb,
  from = 0,
  first = 0,
  titleId,
  listRef,
}: {
  crumb: MiniNavCrumb[];
  from?: number;
  first?: number;
  titleId?: string;
  listRef?: React.Ref<HTMLOListElement>;
}) {
  const last = crumb.length - 1;
  // the nearest place folded away: the "…" steps back to it
  const folded = from > first ? (crumb[from - 1] ?? null) : null;
  return (
    <ol ref={listRef} className="mn-crumb">
      {folded ? (
        <li className="mn-crumb__item mn-crumb__fold">
          {folded.onClick ? (
            <button type="button" className="mn-crumb__more" onClick={folded.onClick}>
              <span aria-hidden="true">…</span>
              <span className="mn-vh">back to {folded.label}</span>
            </button>
          ) : (
            <span className="mn-crumb__more" aria-hidden="true">
              …
            </span>
          )}
        </li>
      ) : null}
      {crumb.map((c, i) => {
        // folded away whole: still laid out (so it can be measured), never seen, reached or read
        const gone = i < from && i < last;
        return (
          <li
            key={`${i}:${c.label}`}
            className="mn-crumb__item"
            data-here={i === last || undefined}
            data-gone={gone || undefined}
            data-lead={(i === from && !folded) || undefined}
            inert={gone || undefined}
          >
            {i > 0 && (
              <span className="mn-crumb__sep" aria-hidden="true">
                /
              </span>
            )}
            {c.onClick && i !== last ? (
              <button type="button" className="mn-crumb__step" onClick={c.onClick}>
                {c.label}
              </button>
            ) : (
              <span className="mn-crumb__here" id={i === last ? titleId : undefined} aria-current={i === last ? "location" : undefined}>
                {c.label}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function MiniNavLine({
  crumb,
  prev,
  next,
  position,
  titleId,
  className,
}: Pick<MiniNavProps, "crumb" | "prev" | "next" | "position" | "titleId" | "className">) {
  const { olRef, from, first } = useCrumbFit(crumb);
  return (
    <div className={`mn-line${className ? ` ${className}` : ""}`}>
      <MiniNavTrail crumb={crumb} from={from} first={first} titleId={titleId} listRef={olRef} />
      {(prev || next) && (
        <div className="mn-steps">
          <button type="button" className="mn-step" onClick={prev?.onClick} disabled={!prev}>
            <Chev dir={-1} />
            <span className="mn-vh">{stepWords(prev, "previous", "nothing before this")}</span>
          </button>
          {position && position.total > 1 && position.index >= 0 ? (
            <span className="mn-steps__pos" aria-live="polite">
              {position.index + 1} of {position.total}
            </span>
          ) : null}
          <button type="button" className="mn-step" onClick={next?.onClick} disabled={!next}>
            <Chev dir={1} />
            <span className="mn-vh">{stepWords(next, "next", "nothing after this")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── the ticker ───────────────────────────────────────────────────────────────

/**
 * The strip a ticker rides on: the first copy of every word measured from the
 * DOM, a glide armed only once THIS set has been measured (else it eases in
 * from 0), and the drag (4px to start, 44px to commit). Shared by the
 * siblings' ticker and the sections' navigator, so the two read as one
 * instrument.
 */
function useTickerStrip(n: number, activeIdx: number, keysSig: string, pick: (to: number, dir: 1 | -1) => void) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [offsets, setOffsets] = useState<number[]>([]);
  const [armed, setArmed] = useState<string | null>(null);
  const [dragDx, setDragDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ id: number; x0: number; moved: boolean } | null>(null);
  const dragged = useRef(false);

  useIsoLayoutEffect(() => {
    let alive = true;
    let raf = 0;
    const measure = () => {
      const strip = stripRef.current;
      if (!alive || !strip) return;
      const kids = Array.from(strip.children).slice(0, n) as HTMLElement[];
      if (kids.length < n) return;
      const next = kids.map((k) => k.offsetLeft);
      setOffsets((cur) => (cur.length === next.length && cur.every((v, i) => v === next[i]) ? cur : next));
    };
    measure();
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => alive && setArmed(keysSig));
    });
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (ro && stripRef.current) ro.observe(stripRef.current);
    if (ro && railRef.current) ro.observe(railRef.current);
    if (typeof document !== "undefined" && "fonts" in document) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [n, keysSig]);

  const end = (e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    try {
      railRef.current?.releasePointerCapture(d.id);
    } catch {
      /* it was never captured */
    }
    if (d.moved) {
      const dir = commit ? tickerStep(e.clientX - d.x0) : 0;
      if (dir) pick(wrapIndex(activeIdx, dir, n), dir);
      // the click that ends a drag must not also pick a word
      window.setTimeout(() => (dragged.current = false), 0);
    }
    setDragDx(0);
    setDragging(false);
  };

  const handlers = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || n < 2) return;
      drag.current = { id: e.pointerId, x0: e.clientX, moved: false };
      dragged.current = false;
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x0;
      if (!d.moved && Math.abs(dx) > TICKER_DRAG_START) {
        d.moved = true;
        dragged.current = true;
        setDragging(true);
        try {
          railRef.current?.setPointerCapture(d.id);
        } catch {
          /* best effort */
        }
      }
      if (d.moved) setDragDx(dx);
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => end(e, true),
    // a cancelled or stolen pointer never leaves the strip half-dragged
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => end(e, false),
    // only the strip's OWN capture ending counts: when the strip takes the
    // pointer from a word (the shell's swipe captures on every press, and a
    // portal's events bubble to it), the word's lost capture bubbles up here
    // and must not end the drag it just started
    onLostPointerCapture: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.target === railRef.current) end(e, false);
    },
  };

  const ready = offsets.length >= n && activeIdx < offsets.length;
  const x = -(ready ? offsets[activeIdx] : 0) + dragDx;
  const still = armed !== keysSig || dragging || !ready;
  return { railRef, stripRef, x, still, dragging, dragged, handlers };
}

/**
 * The SECTIONS of a sheet as the navigator (law_sheet_rises, 2026-10-08): the
 * dashboard's own instrument at sheet size. The open section leads at the left
 * in full ink, the rest trail and fade, the ones before it ride again on the
 * right; tap a word, drag the strip, or ← → (the sheet's one key handler) to
 * move. A real tablist: the first copy of each word is its tab (roving
 * tabindex, selection follows focus), the wrapped copies are drawn only.
 */
export function MiniNavSections({
  sections,
  section,
  onSection,
  className,
}: Pick<MiniNavProps, "sections" | "section" | "onSection" | "className">) {
  const list = sections ?? [];
  const n = list.length;
  const activeIdx = Math.max(0, list.findIndex((s) => s.id === section));
  const keysSig = list.map((s) => s.id).join("\u0000");
  const pick = useCallback(
    (to: number) => {
      if (to < 0 || to >= n || to === activeIdx) return;
      onSection?.(list[to].id);
    },
    [activeIdx, n, onSection, list],
  );
  const { railRef, stripRef, x, still, dragging, dragged, handlers } = useTickerStrip(n, activeIdx, keysSig, pick);
  if (n < 2) return null;

  const words = tickerWords(n, activeIdx);
  const onKey = (e: React.KeyboardEvent) => {
    let to = -1;
    if (e.key === "ArrowRight") to = (activeIdx + 1) % n;
    else if (e.key === "ArrowLeft") to = (activeIdx - 1 + n) % n;
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = n - 1;
    if (to < 0) return;
    // the tabs own these arrows: the sheet's step stands aside
    e.preventDefault();
    pick(to);
    // focus rides with the open word (it leads at the left once it lands)
    requestAnimationFrame(() => {
      const tab = railRef.current?.querySelector<HTMLElement>(`[data-tab-i="${to}"]`);
      tab?.focus({ preventScroll: true });
    });
  };

  return (
    <div
      ref={railRef}
      className={`mn-rail mn-sections${className ? ` ${className}` : ""}`}
      role="tablist"
      aria-label="sections"
      aria-orientation="horizontal"
      data-hl-near="28"
      data-nodrag=""
      data-dragging={dragging || undefined}
      onKeyDown={onKey}
      {...handlers}
    >
      <div ref={stripRef} className="mn-strip" data-still={still || undefined} style={{ transform: `translateX(${x}px)` }}>
        {words.map((w) => {
          const s = list[w.i];
          const real = !w.wrapped;
          return (
            <button
              key={`${s.id}-${w.wrapped ? "w" : "a"}`}
              type="button"
              role={real ? "tab" : undefined}
              aria-selected={real ? w.active : undefined}
              aria-hidden={real ? undefined : true}
              tabIndex={real && w.active ? 0 : -1}
              data-tab-i={real ? w.i : undefined}
              className="os-sib-word mn-word"
              data-hl-target=""
              data-active={w.active ? "" : undefined}
              data-past={w.past ? "" : undefined}
              style={{ ["--nw-op" as string]: w.op, filter: w.blur ? `blur(${w.blur}px)` : undefined } as React.CSSProperties}
              onClick={() => {
                if (dragged.current) return;
                pick(w.i);
              }}
            >
              {s.label}
              {s.n != null ? <span className="mn-word__n">{s.n.toLocaleString("en-US")}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MiniNavTicker({
  siblings,
  active,
  onPick,
  className,
}: {
  siblings: MiniNavSibling[];
  active: string | null | undefined;
  onPick?: (key: string, dir: 1 | -1) => void;
  className?: string;
}) {
  const n = siblings.length;
  const activeIdx = Math.max(0, siblings.findIndex((s) => s.key === active));
  const keysSig = siblings.map((s) => s.key).join("\u0000");

  const pick = useCallback(
    (to: number, dir: 1 | -1) => {
      if (to < 0 || to >= n || to === activeIdx) return;
      onPick?.(siblings[to].key, dir);
    },
    [activeIdx, n, onPick, siblings],
  );
  const { railRef, stripRef, x, still, dragging, dragged, handlers } = useTickerStrip(n, activeIdx, keysSig, pick);
  const words = tickerWords(n, activeIdx);

  return (
    <div
      ref={railRef}
      className={`mn-rail${className ? ` ${className}` : ""}`}
      // a visual twin of the steps (which carry the names for a screen reader);
      // the words stay out of the tab order and the accessibility tree
      aria-hidden="true"
      data-hl-near="28"
      data-nodrag=""
      data-dragging={dragging || undefined}
      {...handlers}
    >
      <div ref={stripRef} className="mn-strip" data-still={still || undefined} style={{ transform: `translateX(${x}px)` }}>
        {words.map((w) => {
          const s = siblings[w.i];
          const dir: 1 | -1 = w.past ? -1 : 1;
          return (
            <button
              key={`${s.key}-${w.wrapped ? "w" : "a"}`}
              type="button"
              tabIndex={-1}
              className="os-sib-word mn-word"
              data-hl-target=""
              data-active={w.active ? "" : undefined}
              data-past={w.past ? "" : undefined}
              style={{ ["--nw-op" as string]: w.op, filter: w.blur ? `blur(${w.blur}px)` : undefined } as React.CSSProperties}
              onClick={() => {
                if (dragged.current) return;
                pick(w.i, dir);
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── the sections ─────────────────────────────────────────────────────────────

export function MiniNavTabs({
  sections,
  section,
  onSection,
  className,
}: Pick<MiniNavProps, "sections" | "section" | "onSection" | "className">) {
  const tabsRef = useRef<HTMLDivElement | null>(null);
  if (!sections || sections.length < 2) return null;

  const onTabKey = (e: React.KeyboardEvent) => {
    if (!onSection) return;
    const i = Math.max(0, sections.findIndex((s) => s.id === section));
    let to = -1;
    if (e.key === "ArrowRight") to = (i + 1) % sections.length;
    else if (e.key === "ArrowLeft") to = (i - 1 + sections.length) % sections.length;
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = sections.length - 1;
    if (to < 0) return;
    // the chips own these arrows (the tab pattern): the sheet's step stands aside
    e.preventDefault();
    onSection(sections[to].id);
    const btn = tabsRef.current?.children[to] as HTMLElement | undefined;
    btn?.focus();
  };

  return (
    <div
      ref={tabsRef}
      className={`mn-tabs${className ? ` ${className}` : ""}`}
      role="tablist"
      aria-label="sections"
      onKeyDown={onTabKey}
      // a phone scrolls a long row sideways: the sheet's pull never starts here
      data-nodrag=""
      data-hscroll=""
    >
      {sections.map((s) => {
        const on = s.id === section;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            className="chip mn-tab"
            data-on={on ? "true" : undefined}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onSection?.(s.id)}
          >
            {s.label}
            {s.n != null && <span className="mn-tab__n">{s.n.toLocaleString("en-US")}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── standalone ───────────────────────────────────────────────────────────────

export default function MiniNav({
  crumb,
  sections,
  section,
  onSection,
  prev,
  next,
  position,
  keys = true,
  titleId,
  className,
  stepKey,
  siblings,
  active,
  onPick,
}: MiniNavProps) {
  const stepping = keys && (!!prev || !!next);

  useEffect(() => {
    if (!stepping) return;
    const on = (e: KeyboardEvent) => {
      const dir = stepFromKey(e, e.target instanceof Element ? (e.target as HTMLElement) : null);
      if (!dir) return;
      const step = dir < 0 ? prev : next;
      if (!step) return;
      e.preventDefault();
      step.onClick();
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [stepping, prev, next]);

  return (
    <nav className={`mn${className ? ` ${className}` : ""}`} aria-label="in this sheet" data-noswipe="">
      <MiniNavLine crumb={crumb} prev={prev} next={next} position={position} titleId={titleId} />
      {siblings && siblings.length > 1 ? <MiniNavTicker siblings={siblings} active={active ?? stepKey} onPick={onPick} /> : null}
      <MiniNavTabs sections={sections} section={section} onSection={onSection} />
    </nav>
  );
}
